/**
 * Persistent Auth Storage - Stockage natif pour les sessions Supabase
 * 
 * Sur Median Native: utilise Native Datastore (UserDefaults/SharedPreferences)
 * → Les données survivent au clearCache WebView
 * 
 * Sur Web: utilise localStorage (fallback)
 */

import { Session } from '@supabase/supabase-js';
import { storageService } from '../storage/storageService';

export interface StoredAuthSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  user_id: string;
  stored_at: number;
}

const AUTH_SESSION_KEY = 'koken.authSession';
const SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

export const persistentAuthStorage = {
  /**
   * Sauvegarde la session Supabase dans le storage natif
   */
  async saveSession(session: Session): Promise<void> {
    if (!session?.access_token || !session?.refresh_token) {
      console.warn('[PersistentAuth] Invalid session, skipping save');
      return;
    }

    const storedSession: StoredAuthSession = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at,
      user_id: session.user?.id || '',
      stored_at: Date.now()
    };

    try {
      await storageService.set(AUTH_SESSION_KEY, storedSession);
      console.log('[PersistentAuth] Session saved to native storage');
    } catch (error) {
      console.error('[PersistentAuth] Failed to save session:', error);
    }
  },

  /**
   * Récupère la session stockée
   */
  async getSession(): Promise<StoredAuthSession | null> {
    try {
      const stored = await storageService.get<StoredAuthSession>(AUTH_SESSION_KEY);
      
      if (!stored) {
        return null;
      }

      // Vérifier si la session n'est pas trop vieille
      const age = Date.now() - stored.stored_at;
      if (age > SESSION_MAX_AGE_MS) {
        console.log('[PersistentAuth] Stored session too old, clearing');
        await this.clearSession();
        return null;
      }

      // Vérifier si la session n'est pas expirée
      if (stored.expires_at && stored.expires_at * 1000 < Date.now()) {
        // La session est expirée mais on peut encore essayer de refresh
        console.log('[PersistentAuth] Session expired but has refresh token');
      }

      return stored;
    } catch (error) {
      console.error('[PersistentAuth] Failed to get session:', error);
      return null;
    }
  },

  /**
   * Supprime la session stockée avec retry et vérification
   * @returns true si le nettoyage a réussi, false sinon
   */
  async clearSession(): Promise<boolean> {
    const MAX_RETRIES = 3;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await storageService.remove(AUTH_SESSION_KEY);
        
        // ✅ FIX: Vérifier que le nettoyage a bien fonctionné
        const checkValue = await storageService.get<StoredAuthSession>(AUTH_SESSION_KEY);
        
        if (!checkValue) {
          console.log(`[PersistentAuth] Session cleared successfully on attempt ${attempt}`);
          return true;
        }
        
        console.warn(`[PersistentAuth] Session still present after removal attempt ${attempt}`);
        
        // Attendre un peu avant de retry
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 100 * attempt));
        }
      } catch (error) {
        console.error(`[PersistentAuth] Failed to clear session on attempt ${attempt}:`, error);
      }
    }
    
    console.error('[PersistentAuth] ❌ Failed to clear session after all retries');
    return false;
  },

  /**
   * Vérifie si le storage natif est utilisé
   */
  isNativeStorage(): boolean {
    return storageService.isNativeStorage;
  }
};
